import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import type { SupportStorageFileItem, SupportStorageStats } from '../services/types';

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = -1;
  do {
    v /= 1024;
    i += 1;
  } while (v >= 1024 && i < units.length - 1);
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function kindLabel(kind: string): string {
  if (kind === 'image') return 'Фото';
  if (kind === 'video') return 'Видео';
  if (kind === 'voice') return 'Голос';
  return 'Файл';
}

export function SupportStoragePanel() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<SupportStorageStats | null>(null);
  const [files, setFiles] = useState<SupportStorageFileItem[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [kind, setKind] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SupportStorageFileItem | null>(null);
  const [olderDays, setOlderDays] = useState(30);

  const loadStats = useCallback(async () => {
    const res = await api.getSupportStorageStats();
    if (res.data) setStats(res.data);
  }, []);

  const loadFiles = useCallback(async () => {
    const res = await api.listSupportStorageFiles({
      page,
      page_size: 30,
      kind: kind || undefined,
    });
    if (res.data) {
      setFiles(res.data.items);
      setPages(res.data.pages);
    } else if (res.error) {
      setError(res.error.message);
    }
  }, [page, kind]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!open) return;
    void loadFiles();
  }, [open, loadFiles]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const cleanupSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Удалить ${selected.size} файл(ов) с диска?`)) return;
    setBusy(true);
    setError(null);
    const res = await api.cleanupSupportStorage({ keys: [...selected] });
    setBusy(false);
    if (res.error) {
      setError(res.error.message);
      return;
    }
    setSelected(new Set());
    await Promise.all([loadStats(), loadFiles()]);
  };

  const cleanupOlder = async () => {
    if (!window.confirm(`Удалить все медиа старше ${olderDays} дн.?`)) return;
    setBusy(true);
    setError(null);
    const res = await api.cleanupSupportStorage({
      older_than_days: olderDays,
      kind: kind || 'all',
    });
    setBusy(false);
    if (res.error) {
      setError(res.error.message);
      return;
    }
    setSelected(new Set());
    await Promise.all([loadStats(), loadFiles()]);
  };

  const pct = stats?.used_percent ?? 0;

  return (
    <div className="support-storage-panel">
      <button type="button" className="support-storage-toggle" onClick={() => setOpen((v) => !v)}>
        <span>Хранилище медиа</span>
        {stats ? (
          <span className="muted">
            {formatBytes(stats.used_bytes)} / {formatBytes(stats.total_bytes)} · {pct}%
          </span>
        ) : (
          <span className="muted">{open ? '…' : 'показать'}</span>
        )}
      </button>

      {open && (
        <div className="support-storage-body card">
          {stats && (
            <div className="support-storage-hud">
              <div className="support-storage-bar" aria-hidden>
                <div
                  className={`support-storage-bar-fill${pct >= 90 ? ' is-critical' : pct >= 70 ? ' is-warn' : ''}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              <div className="support-storage-meta">
                <span>
                  Занято <strong>{formatBytes(stats.used_bytes)}</strong>
                </span>
                <span>
                  Свободно <strong>{formatBytes(stats.free_bytes)}</strong>
                </span>
                <span>
                  <strong>{pct}%</strong> · {stats.file_count} файл.
                </span>
              </div>
            </div>
          )}

          <div className="support-storage-toolbar">
            <label className="support-storage-filter" htmlFor="support-storage-kind">
              <span className="sr-only">Тип файлов</span>
              <select
                id="support-storage-kind"
                name="storage_kind"
                value={kind}
                onChange={(e) => {
                  setPage(1);
                  setKind(e.target.value);
                }}
              >
                <option value="">Все типы</option>
                <option value="image">Фото</option>
                <option value="video">Видео</option>
                <option value="voice">Голос</option>
              </select>
            </label>
            <label className="support-storage-older" htmlFor="support-storage-older-days">
              Старше
              <input
                id="support-storage-older-days"
                name="older_days"
                type="number"
                min={1}
                max={3650}
                value={olderDays}
                onChange={(e) => setOlderDays(Math.max(1, Number(e.target.value) || 1))}
              />
              дн.
            </label>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void cleanupOlder()}>
              Очистить старые
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || selected.size === 0}
              onClick={() => void cleanupSelected()}
            >
              Удалить выбранные ({selected.size})
            </button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => { void loadStats(); void loadFiles(); }}>
              Обновить
            </button>
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="support-storage-list">
            {files.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                Файлов нет
              </p>
            ) : (
              files.map((f) => (
                <div key={f.key} className="support-storage-row">
                  <input
                    id={`support-storage-file-${f.key}`}
                    name="storage_files"
                    type="checkbox"
                    checked={selected.has(f.key)}
                    onChange={() => toggle(f.key)}
                    aria-label={`Выбрать ${f.name}`}
                  />
                  <button
                    type="button"
                    className="support-storage-preview-btn"
                    onClick={() => setPreview(f)}
                    title="Просмотр"
                  >
                    {f.kind === 'image' ? (
                      <img src={api.supportStorageFileUrl(f.key)} alt="" />
                    ) : (
                      <span>{kindLabel(f.kind)}</span>
                    )}
                  </button>
                  <label className="support-storage-row-meta" htmlFor={`support-storage-file-${f.key}`}>
                    <strong>
                      {f.ticket_id != null ? `#${f.ticket_id}` : '—'} · {f.name}
                    </strong>
                    <span className="muted">
                      {formatBytes(f.size)} · {f.mtime.replace('T', ' ').slice(0, 16)}
                    </span>
                  </label>
                </div>
              ))
            )}
          </div>

          {pages > 1 && (
            <div className="support-storage-pager">
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ←
              </button>
              <span className="muted">
                {page} / {pages}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                →
              </button>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="support-admin-lightbox" role="presentation" onClick={() => setPreview(null)}>
          <div className="support-storage-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)}>
              Закрыть
            </button>
            {preview.kind === 'image' || preview.kind === 'video' ? (
              preview.kind === 'video' ? (
                <video src={api.supportStorageFileUrl(preview.key)} controls autoPlay />
              ) : (
                <img src={api.supportStorageFileUrl(preview.key)} alt={preview.name} />
              )
            ) : preview.kind === 'voice' ? (
              <audio src={api.supportStorageFileUrl(preview.key)} controls autoPlay />
            ) : (
              <p className="muted">{preview.name}</p>
            )}
            <p className="muted">
              {preview.key} · {formatBytes(preview.size)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
